import { z } from 'zod';

/**
 * The app's environment contract. Exported so it can be tested without depending
 * on the ambient `import.meta.env`.
 *
 * The Auth0 fields are optional here even though the app cannot authenticate
 * without them: this schema is parsed eagerly at module load, and `task build`
 * runs in CI with none of them set. `packages/auth` is what asserts their
 * presence when it constructs the Auth0 provider, which is where a missing value
 * can produce an error message that names the missing variable.
 *
 * Values come from Vercel project env vars, declared in `infra/terraform/auth0.tf`.
 * All three are public by design — a PKCE SPA ships them in its bundle.
 */
export const envSchema = z.object({
  MODE: z.enum(['development', 'test', 'production']).default('development'),

  /**
   * Auth0 custom domain for the tenant this deploy target authenticates against.
   *
   * A bare hostname — `identity-dev.signaladvisors.com`, never
   * `https://identity-dev.signaladvisors.com`. The Auth0 SDK builds its authorize
   * URL by prefixing a scheme itself, so a value carrying one produces a broken
   * endpoint at runtime. `.min(1)` alone accepted a URL, `not a domain`, and even
   * whitespace; failing here names the variable instead.
   */
  VITE_AUTH0_DOMAIN: z
    .string()
    .regex(
      /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i,
      'must be a bare hostname, e.g. identity-dev.signaladvisors.com (no scheme, no path)',
    )
    .optional(),
  /**
   * Public client_id of the `Advisor Portal Web` application in that tenant —
   * this app shares the portal's Auth0 client rather than having its own, see
   * ADR 015. Differs per tenant.
   */
  VITE_AUTH0_CLIENT_ID: z.string().min(1).optional(),
  /**
   * API audience tokens are requested for (the apis-fleet gateway).
   *
   * `z.url()`, not `z.string().url()` — this repo is on Zod 4, where the method
   * form is deprecated in favour of the top-level format validator.
   */
  VITE_AUTH0_AUDIENCE: z.url().optional(),

  /**
   * Origin of the apis-fleet API gateway this deploy target calls
   * (`packages/api-client`, FP-4 Plan C).
   *
   * NOT the same thing as `VITE_AUTH0_AUDIENCE`, even though the two strings are
   * identical in dev. The audience is an opaque resource-server identifier that
   * happens to be spelled as this host; this is a URL requests are sent to. They
   * are free to diverge — the gateway already accepts two audiences on this one
   * host (apis `infrastructure/dev.tfvars:11`) — so deriving one from the other
   * would make that divergence a silent bug.
   *
   * `z.url()`, not `z.string().url()` — this repo is on Zod 4.
   */
  VITE_API_BASE_URL: z.url().optional(),
});

export const env = envSchema.parse({
  MODE: import.meta.env.MODE,
  VITE_AUTH0_DOMAIN: import.meta.env.VITE_AUTH0_DOMAIN,
  VITE_AUTH0_CLIENT_ID: import.meta.env.VITE_AUTH0_CLIENT_ID,
  VITE_AUTH0_AUDIENCE: import.meta.env.VITE_AUTH0_AUDIENCE,
  VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
});
export type Env = z.infer<typeof envSchema>;
